import { keepPreviousData, type Query, type QueryKey, useQuery } from '@tanstack/react-query'
import { z } from 'zod'

import { WINDOW_STEP_MS } from './model'

import { isApiError } from '@/api'
import {
  getMeetingReports,
  getReportCohorts,
  getReportGroups,
  getReportOverview,
  type ReportDimension,
  type ReportFilters,
} from '@/api/meetingReportsObservability'

export const QUERY_ROOT = 'reported-meetings'

export const isAccessDenied = (error: unknown) => isApiError(error) && [401, 403].includes(error.status)

/** The page restamps its trailing window this often, which re-keys every query. */
export const REFRESH_MS = WINDOW_STEP_MS

/** One retry for server/network failures; none for 4xx or schema mismatches, which are deterministic. */
export const shouldRetry = (failureCount: number, error: unknown) =>
  failureCount < 1 &&
  !(isApiError(error) && error.status >= 400 && error.status < 500) &&
  !(error instanceof z.ZodError)

const queryDefaults = {
  staleTime: 30_000,
  gcTime: 0,
  retry: shouldRetry,
} as const

const withoutWindow = (filters: ReportFilters) =>
  Object.fromEntries(Object.entries(filters).filter(([key]) => key !== 'start_at' && key !== 'end_at'))

/** Same population, ignoring the restamped window bounds. */
export const sameScope = (a: ReportFilters, b: ReportFilters) =>
  JSON.stringify(withoutWindow(a)) === JSON.stringify(withoutWindow(b))

/**
 * Like `keepPreviousData`, but only while the row (filters minus window bounds) is unchanged, so a
 * window restamp or a cursor change keeps the current rows while a different row starts from a
 * skeleton rather than showing the previous row's data under its heading.
 */
const keepSameScopeData =
  (filters: ReportFilters) =>
  <T>(previousData: T | undefined, previousQuery: Query<T, Error, T, QueryKey> | undefined) => {
    const previousFilters = previousQuery?.queryKey[2]
    return previousFilters && sameScope(previousFilters as ReportFilters, filters) ? previousData : undefined
  }

export const useReportOverview = (filters: ReportFilters) =>
  useQuery({
    ...queryDefaults,
    placeholderData: keepPreviousData,
    queryKey: [QUERY_ROOT, 'overview', filters],
    queryFn: ({ signal }) => getReportOverview(filters, signal),
  })

export const useReportGroups = (filters: ReportFilters, groupBy: ReportDimension) =>
  useQuery({
    ...queryDefaults,
    placeholderData: keepPreviousData,
    queryKey: [QUERY_ROOT, 'groups', filters, groupBy],
    queryFn: ({ signal }) => getReportGroups(filters, groupBy, signal),
  })

export const useReportCohorts = (filters: ReportFilters, enabled: boolean) =>
  useQuery({
    ...queryDefaults,
    enabled,
    placeholderData: keepSameScopeData(filters),
    queryKey: [QUERY_ROOT, 'cohorts', filters],
    queryFn: ({ signal }) => getReportCohorts(filters, signal),
  })

export const useMeetingReports = (filters: ReportFilters, cursor: string | undefined, enabled: boolean) =>
  useQuery({
    ...queryDefaults,
    enabled,
    placeholderData: keepSameScopeData(filters),
    queryKey: [QUERY_ROOT, 'reports', filters, cursor],
    queryFn: ({ signal }) => getMeetingReports(filters, cursor, signal),
  })
